@file:OptIn(ExperimentalLayoutApi::class)

package music.jammers.client.shared.event.presentation.ui

import androidx.compose.animation.core.LinearOutSlowInEasing
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.material3.pulltorefresh.PullToRefreshDefaults
import androidx.compose.material3.pulltorefresh.pullToRefresh
import androidx.compose.material3.pulltorefresh.rememberPullToRefreshState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import limasoul.client.shared.generated.resources.Res
import limasoul.client.shared.generated.resources.event_list_screen_title
import limasoul.client.shared.generated.resources.event_list_screen_tracks
import music.jammers.client.shared.component.CenterBox
import music.jammers.client.shared.component.ErrorDialog
import music.jammers.client.shared.component.JammersTopAppBar
import music.jammers.client.shared.component.Progress
import music.jammers.client.shared.component.avatar.presentation.ui.AvatarImageComponent
import music.jammers.client.shared.event.di.EventComponent
import music.jammers.client.shared.event.presentation.model.EventListItem
import music.jammers.client.shared.event.presentation.viewmodel.EventListViewModel
import org.jetbrains.compose.resources.stringResource

const val EVENT_LIST_SCREEN = "EventListScreen"

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EventListScreen() {
    val viewModel: EventListViewModel = remember { EventComponent.eventListViewModel.apply { onReady() } }
    val eventListState = viewModel.state.value

    val pullRefreshState = rememberPullToRefreshState()

    val scaleFraction = {
        if (pullRefreshState.isAnimating) {
            1f
        } else {
            LinearOutSlowInEasing.transform(pullRefreshState.distanceFraction).coerceIn(0f, 1f)
        }
    }

    Scaffold(
        topBar = {
            JammersTopAppBar(
                text = stringResource(Res.string.event_list_screen_title).uppercase(),
                actions = {
                    AvatarImageComponent()
                }
            )
        },
        floatingActionButton = {
            if (eventListState.isAdmin) {
                FloatingActionButton(
                    onClick = { viewModel.onAddEventClicked() },
                    shape = CircleShape,
                ) {
                    Icon(
                        imageVector = Icons.Filled.Add,
                        contentDescription = "Add event",
                    )
                }
            }
        },
        modifier = Modifier.pullToRefresh(
            state = pullRefreshState,
            isRefreshing = eventListState.progress,
            onRefresh = {
                viewModel.onReady()
            }
        )
    ) { paddingValues: PaddingValues ->
        CenterBox {
            Box(
                modifier = Modifier.fillMaxSize()
                    .padding(paddingValues)
            ) {
                if (eventListState.progress) {
                    Progress()
                } else {
                    EventList(eventListState.eventList, viewModel)

                    eventListState.error?.let { error ->
                        ErrorDialog(error.message ?: "Unknown") {
                            viewModel.onErrorShown()
                        }
                    }
                }

                Box(
                    modifier = Modifier.align(Alignment.TopCenter)
                        .graphicsLayer {
                            scaleX = scaleFraction()
                            scaleY = scaleFraction()
                        }
                ) {
                    PullToRefreshDefaults.Indicator(
                        state = pullRefreshState,
                        isRefreshing = eventListState.progress,
                    )
                }
            }
        }
    }
}

@Composable
private fun EventList(eventList: List<EventListItem>, viewModel: EventListViewModel) {
    if (eventList.isEmpty()) {
        Column(Modifier.verticalScroll(rememberScrollState())) {
            Box(Modifier.fillMaxSize())
        }
    } else {
        LazyColumn(modifier = Modifier) {
            items(eventList) { event ->
                EventCard(event) { clickedEvent ->
                    viewModel.onCardClicked(clickedEvent)
                }
            }
        }
    }
}

@Composable
private fun EventCard(event: EventListItem, onClick: (EventListItem) -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp)
            .clickable {
                onClick(event)
            },
        colors = CardDefaults.cardColors(
            containerColor = Color.Transparent,
        )
    ) {
        val density = LocalDensity.current
        val minLoyaltyCardHeight = 150.dp
        var contentHeightDp by remember { mutableStateOf(minLoyaltyCardHeight) }

        Box(
            modifier = Modifier.fillMaxWidth()
                .defaultMinSize(
                    minHeight = minLoyaltyCardHeight
                )
                .onGloballyPositioned { position ->
                    contentHeightDp = density.run {
                        position.size
                            .height
                            .toDp()
                            .coerceAtLeast(minLoyaltyCardHeight)
                    }
                }
        ) {
            AsyncImage(
                model = event.backgroundUrl,
                modifier = Modifier.fillMaxWidth()
                    .height(contentHeightDp),
                contentDescription = "event_image",
                contentScale = ContentScale.FillWidth,
            )

            Column(
                modifier = Modifier.padding(
                    horizontal = 24.dp,
                    vertical = 16.dp,
                ).fillMaxHeight()
            ) {
                val textColor = event.textColor

                Row {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(
                            text = event.day,
                            style = MaterialTheme.typography.headlineMedium.copy(
                                color = textColor,
                            ),
                        )
                        Text(
                            text = event.month,
                            style = MaterialTheme.typography.bodyLarge.copy(
                                color = textColor,
                            ),
                        )

                        VerticalDivider(
                            thickness = 2.dp,
                            color = textColor,
                            modifier = Modifier.size(width = 2.dp, height = 50.dp).padding(2.dp),
                        )
                    }

                    Spacer(modifier = Modifier.size(16.dp))

                    Text(
                        text = event.title,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        style = MaterialTheme.typography.headlineLarge.copy(
                            color = textColor,
                        ),
                    )
                }

                Row {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(
                            text = event.hour,
                            style = MaterialTheme.typography.headlineMedium.copy(
                                color = textColor,
                            ),
                        )

                        Text(
                            text = event.minutes,
                            style = MaterialTheme.typography.bodyLarge.copy(
                                color = textColor,
                            ),
                        )
                    }

                    Spacer(modifier = Modifier.size(16.dp))

                    Column {
                        Text(
                            text = event.spot,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            style = MaterialTheme.typography.titleLarge.copy(
                                color = textColor,
                            ),
                        )

                        Text(
                            text = stringResource(
                                Res.string.event_list_screen_tracks,
                                event.readyTracks,
                                event.tracksCount,
                            ),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            color = textColor.copy(alpha = 0.6f),
                            style = MaterialTheme.typography.titleMedium,
                        )
                    }
                }
            }
        }
    }
}